import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import moment from 'moment';
import { KeenIcon } from '@/components';
import {
  FeedbackStatus,
  FeedbackType,
  ModuleType,
  FeedbackJsmRoutingMode,
  FeedbackDestination,
} from '@/types/feedback.types';
import {
  useGetFeedbackByIdQuery,
  useRouteFeedbackMutation,
  useUpdateFeedbackStatusMutation,
} from '@/services/UserFeedbackService';
import { Container } from '@/components/container';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDateTime } from '@/utils/Date';

// Local maps (could be centralized later)
const feedbackTypeMap: Record<FeedbackType, string> = {
  [FeedbackType.ASK_QUESTION]: 'Ask Question',
  [FeedbackType.LEAVE_COMMENT]: 'Leave Comment',
  [FeedbackType.REPORT_BUG]: 'Report Bug',
  [FeedbackType.SUGGEST_IMPROVEMENT]: 'Suggest Improvement',
  [FeedbackType.GENERAL_FEEDBACK]: 'General Feedback',
  [FeedbackType.PRAISE]: 'Praise',
};

const moduleTypeMap: Record<ModuleType, string> = {
  [ModuleType.HARBOR_BOOKING]: 'Harbor Booking',
  [ModuleType.POI]: 'Points of Interest',
  [ModuleType.ROUTING]: 'Routing',
  [ModuleType.GENERAL]: 'General',
};

const feedbackStatusMap: Record<
  FeedbackStatus,
  { label: string; color: string; ring: string }
> = {
  [FeedbackStatus.PENDING]: {
    label: 'Pending',
    color: 'bg-amber-100 text-amber-800',
    ring: 'ring-amber-300',
  },
  [FeedbackStatus.IN_PROGRESS]: {
    label: 'In Progress',
    color: 'bg-blue-100 text-blue-700',
    ring: 'ring-blue-300',
  },
  [FeedbackStatus.RESOLVED]: {
    label: 'Resolved',
    color: 'bg-emerald-100 text-emerald-700',
    ring: 'ring-emerald-300',
  },
  [FeedbackStatus.CLOSED]: {
    label: 'Closed',
    color: 'bg-gray-100 text-gray-700',
    ring: 'ring-gray-300',
  },
  [FeedbackStatus.REJECTED]: {
    label: 'Rejected',
    color: 'bg-red-100 text-red-700',
    ring: 'ring-red-300',
  },
};

const statusOrder: FeedbackStatus[] = [
  FeedbackStatus.PENDING,
  FeedbackStatus.IN_PROGRESS,
  FeedbackStatus.RESOLVED,
  FeedbackStatus.CLOSED,
  FeedbackStatus.REJECTED,
];

const UserFeedbackDetailsPage = () => {
  const { feedbackId } = useParams<{ feedbackId: string }>();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const {
    data: feedback,
    isLoading,
    isError,
    refetch,
  } = useGetFeedbackByIdQuery(feedbackId || '', { skip: !feedbackId });

  const [updateStatus, { isLoading: isUpdating }] =
    useUpdateFeedbackStatusMutation();
  const [routeFeedback, { isLoading: isRouting }] = useRouteFeedbackMutation();

  const [status, setStatus] = useState<FeedbackStatus | null>(null);
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [routeError, setRouteError] = useState<string>('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (feedback) {
      setStatus(feedback.status);
      setAdminNotes(feedback.admin_notes || '');
    }
  }, [feedback]);

  const hasChanges = () => {
    if (!feedback) return false;
    return (
      (status !== null && status !== feedback.status) ||
      adminNotes !== (feedback.admin_notes || '')
    );
  };

  const handleSave = useCallback(async () => {
    if (!feedbackId || !feedback || !hasChanges()) return;
    setSaving(true);
    setError('');
    try {
      await updateStatus({
        id: feedbackId,
        payload: {
          status: status ?? feedback.status,
          admin_notes: adminNotes || null,
        },
      }).unwrap();
      await refetch();
      setMode('view');
    } catch (e) {
      console.warn(e);
      setError('Failed to update feedback. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [adminNotes, feedback, feedbackId, refetch, status, updateStatus]);

  const handleRouteToJsm = useCallback(async () => {
    if (!feedbackId || !feedback) return;
    setRouteError('');
    try {
      await routeFeedback({
        id: feedbackId,
        payload: {
          destination: FeedbackDestination.JIRA_SERVICE_MANAGEMENT,
          routing_notes:
            adminNotes ||
            'Reviewed by admin and routed to Jira Service Management.',
        },
      }).unwrap();
      await refetch();
    } catch (e) {
      console.warn(e);
      setRouteError('Failed to route feedback to JSM. Please try again.');
    }
  }, [adminNotes, feedback, feedbackId, refetch, routeFeedback]);

  const currentStatusMeta = status
    ? feedbackStatusMap[status]
    : feedback
      ? feedbackStatusMap[feedback.status]
      : null;
  const jiraIssueKey =
    feedback?.jira_issue_key || feedback?.jira_info?.issue_key || null;
  const jiraIssueUrl =
    feedback?.jira_issue_url || feedback?.jira_info?.issue_url || null;
  const jiraSyncError =
    feedback?.jira_sync_error || feedback?.jira_info?.sync_error || null;
  const jiraStatus =
    feedback?.jira_info?.status || feedback?.jira_status || null;

  return (
    <div className="w-full min-h-[calc(100vh-4rem)] pb-10">
      <Container>
        <div className="flex items-center gap-3 pt-6 pb-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            aria-label="Back to previous page"
          >
            <KeenIcon icon="black-left" className="text-xl" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              Feedback Details
              {currentStatusMeta && (
                <span
                  className={clsx(
                    'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ring-2',
                    currentStatusMeta.color,
                    currentStatusMeta.ring
                  )}
                >
                  {feedbackStatusMap[(status ?? feedback?.status)!].label}
                </span>
              )}
            </h1>
            {feedback && (
              <p className="text-sm text-gray-600 mt-1">
                Submitted {moment(feedback.created_at).fromNow()}
              </p>
            )}
          </div>
          {mode === 'view' ? (
            hasPermission('feedback_management_update') && (
              <button
                onClick={() => setMode('edit')}
                disabled={!feedback}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary  text-white text-sm font-medium shadow-sm hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Edit feedback"
              >
                <KeenIcon icon="pencil" className="text-base" /> Edit
              </button>
            )
          ) : (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (!feedback) return;
                  setStatus(feedback.status);
                  setAdminNotes(feedback.admin_notes || '');
                  setMode('view');
                  setError('');
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gray-100 border border-gray-300 text-gray-700 text-sm font-medium shadow-sm"
                aria-label="Cancel changes"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges() || saving || isUpdating}
                className={clsx(
                  'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-medium shadow-sm hover:bg-primary-dark',
                  (saving || isUpdating || !hasChanges()) &&
                    'opacity-50 cursor-not-allowed hover:bg-primary'
                )}
                aria-label="Save changes"
              >
                {(saving || isUpdating) && (
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                Save Changes
              </button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-7">
          <div className="xl:col-span-2 space-y-7 border border-black/10 rounded-2xl shadow-lg">
            <div className="bg-gray-100 rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-4 flex items-center gap-4 border-b border-primary/10">
                {feedback?.user?.profile_picture ? (
                  <img
                    src={feedback.user.profile_picture}
                    alt={feedback.user.name || feedback.user.email}
                    className="h-14 w-14 rounded-full object-cover ring-4 ring-white shadow-sm"
                  />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center dark:text-white contrast:text-white text-primary font-bold ring-4 ring-white shadow-sm">
                    {(feedback?.user?.name ||
                      feedback?.user?.email ||
                      'U')[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">
                    {feedback?.user?.name ||
                      feedback?.user?.email ||
                      'Unknown User'}
                  </h2>
                  {feedback?.user?.email && (
                    <p className="text-sm text-gray-600 truncate">
                      {feedback.user.email}
                    </p>
                  )}
                  {feedback && (
                    <p className="text-xs text-gray-500 mt-1">
                      Submitted on {formatDisplayDateTime(feedback.created_at)}
                    </p>
                  )}
                </div>
              </div>
              <div className="px-6 py-6 space-y-8">
                {/* Message */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <KeenIcon
                      icon="message-text"
                      className="text-base text-primary"
                    />{' '}
                    Message
                  </h3>
                  {isLoading ? (
                    <div className="h-32 rounded-lg  animate-pulse" />
                  ) : isError || !feedback ? (
                    <p className="text-sm text-red-500">
                      Failed to load content.
                    </p>
                  ) : (
                    <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 min-h-[120px] text-gray-700 whitespace-pre-wrap leading-relaxed shadow-inner">
                      {feedback.feedback_text || 'No content provided.'}
                    </div>
                  )}
                </section>

                {/* Attachment */}
                {feedback?.media && (
                  <section>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <KeenIcon
                        icon="image"
                        className="text-base text-primary"
                      />{' '}
                      Attachment
                    </h3>
                    <div className="relative group max-w-xs rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img
                        src={feedback.media}
                        alt="Feedback attachment"
                        className="w-full h-48 object-contain bg-gray-50"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                        <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                          Attachment
                        </span>
                      </div>
                    </div>
                  </section>
                )}

                {/* Admin Notes */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    {mode === 'edit' ? (
                      <label
                        htmlFor="admin-notes"
                        className="text-sm font-semibold text-gray-900 flex items-center gap-2"
                      >
                        <KeenIcon
                          icon="file"
                          className="text-base text-primary"
                        />{' '}
                        Admin Notes
                      </label>
                    ) : (
                      <span
                        id="admin-notes-label"
                        className="text-sm font-semibold text-gray-900 flex items-center gap-2"
                      >
                        <KeenIcon
                          icon="file"
                          className="text-base text-primary"
                        />{' '}
                        Admin Notes
                      </span>
                    )}
                    {mode === 'view' && feedback?.admin_notes && (
                      <button
                        onClick={() => setMode('edit')}
                        className="text-sm text-primary dark:text-blue-200 contrast:text-blue-200 hover:text-primary-dark"
                        aria-label="Edit admin notes"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {mode === 'edit' ? (
                    <textarea
                      id="admin-notes"
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add internal notes or resolution details here..."
                      className="w-full min-h-[140px] bg-gray-100 border border-gray-300 rounded-lg p-4 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary shadow-inner"
                    />
                  ) : feedback?.admin_notes ? (
                    <div
                      id="admin-notes"
                      className="break-all p-4 bg-primary/5 border border-primary/20 rounded-lg text-gray-800 min-h-[90px] whitespace-pre-wrap relative"
                      aria-labelledby="admin-notes-label"
                      role="text"
                    >
                      {feedback.admin_notes}
                    </div>
                  ) : (
                    <div
                      id="admin-notes"
                      className="p-4 bg-gray-200 border border-dashed border-gray-300 rounded-lg text-gray-500 italic text-sm"
                      aria-labelledby="admin-notes-label"
                      role="text"
                    >
                      No admin notes added yet.
                    </div>
                  )}
                  {error && (
                    <p className="text-red-500 text-sm mt-2" role="alert">
                      {error}
                    </p>
                  )}
                </section>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-7 ">
            <div className="bg-gray-100 rounded-2xl shadow-sm border border-gray-100 p-6 border border-black/10 rounded-2xl shadow-lg">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Details
              </h3>
              <dl className="space-y-4">
                <div className="flex justify-between gap-4">
                  <dt className="text-xs uppercase tracking-wider text-gray-500">
                    Type
                  </dt>
                  <dd className="text-sm font-medium text-gray-800">
                    {feedback?.feedback_type &&
                      feedbackTypeMap[feedback.feedback_type]}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-xs uppercase tracking-wider text-gray-500">
                    Module
                  </dt>
                  <dd className="text-sm font-medium text-gray-800">
                    {feedback?.module ? moduleTypeMap[feedback.module] : '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-xs uppercase tracking-wider text-gray-500">
                    Created
                  </dt>
                  <dd className="text-sm font-medium text-gray-800">
                    {feedback && formatDisplayDateTime(feedback.created_at)}
                  </dd>
                </div>
                {(feedback?.jira_info?.issue_key ||
                  feedback?.jira_issue_key) && (
                  <>
                    <div className="flex justify-between gap-4">
                      <dt className="text-xs uppercase tracking-wider text-gray-500">
                        JSM ticket
                      </dt>
                      <dd className="text-sm font-medium text-gray-800 text-right">
                        {jiraIssueUrl ? (
                          <a
                            href={jiraIssueUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {jiraIssueKey}
                          </a>
                        ) : (
                          jiraIssueKey
                        )}
                      </dd>
                    </div>
                    {jiraStatus && (
                      <div className="flex justify-between gap-4">
                        <dt className="text-xs uppercase tracking-wider text-gray-500">
                          JSM status
                        </dt>
                        <dd className="text-sm font-medium text-gray-800">
                          {jiraStatus}
                        </dd>
                      </div>
                    )}
                  </>
                )}
              </dl>
              {(feedback?.jira_info?.issue_key ||
                feedback?.jira_issue_key) && (
                <p className="text-xs text-gray-500 mt-4 leading-relaxed">
                  Saaristo status syncs from Jira when the JSM webhook is
                  configured. Customer updates are sent by JSM.
                </p>
              )}
            </div>

            <div className="bg-gray-100 rounded-2xl shadow-sm border border-gray-100 p-6 border border-black/10 rounded-2xl shadow-lg">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                JSM Routing
              </h3>
              <dl className="space-y-4">
                <div className="flex justify-between gap-4">
                  <dt className="text-xs uppercase tracking-wider text-gray-500">
                    Rule
                  </dt>
                  <dd className="text-sm font-medium text-gray-800">
                    {feedback?.jsm_routing_mode ===
                    FeedbackJsmRoutingMode.MANUAL_REVIEW
                      ? 'Manual review'
                      : 'Auto-route'}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-xs uppercase tracking-wider text-gray-500">
                    Issue
                  </dt>
                  <dd className="text-sm font-medium text-gray-800">
                    {jiraIssueKey && jiraIssueUrl ? (
                      <a
                        href={jiraIssueUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        {jiraIssueKey}
                      </a>
                    ) : (
                      jiraIssueKey || '-'
                    )}
                  </dd>
                </div>
                {jiraSyncError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {jiraSyncError}
                  </div>
                )}
              </dl>
            </div>

            <div className="bg-gray-100 rounded-2xl shadow-sm border border-gray-100 p-6 border border-black/10 rounded-2xl shadow-lg">
              <div>
                <label
                  htmlFor="status-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {mode === 'edit' ? 'Update Status' : 'Current Status'}
                </label>
                <div className="relative">
                  <div
                    id="status-select"
                    className={clsx(
                      'w-full h-11 px-4 py-2 border rounded-lg flex items-center justify-between cursor-pointer',
                      mode === 'edit' && 'border-blue-500',
                      (isUpdating || mode !== 'edit') &&
                        'bg-gray-100 cursor-not-allowed'
                    )}
                    onClick={() =>
                      mode === 'edit' &&
                      setShowStatusDropdown(!showStatusDropdown)
                    }
                    aria-label="Select status"
                    aria-haspopup="listbox"
                    aria-expanded={showStatusDropdown}
                    aria-controls="status-listbox"
                    role="combobox"
                  >
                    <div className="truncate">
                      {status !== null
                        ? feedbackStatusMap[status]?.label
                        : feedback
                          ? feedbackStatusMap[feedback.status]?.label
                          : 'Select status'}
                    </div>
                    <div className="flex items-center">
                      <svg
                        className={clsx(
                          'w-4 h-4 text-gray-400 transition-transform duration-200',
                          showStatusDropdown && 'transform rotate-180',
                          (isUpdating || mode !== 'edit') && 'opacity-50'
                        )}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                  {showStatusDropdown && mode === 'edit' && (
                    <div
                      id="status-listbox"
                      role="listbox"
                      className="absolute z-50 w-full mt-1 bg-gray-100 border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {statusOrder.map((statusValue) => {
                        const statusInfo = feedbackStatusMap[statusValue];
                        return (
                          <div
                            key={statusValue}
                            className={clsx(
                              'px-4 py-2 text-sm cursor-pointer hover:bg-gray-100',
                              status === statusValue &&
                                'bg-primary text-white hover:text-black dark:hover:text-white contrast:hover:text-white'
                            )}
                            onClick={() => {
                              setStatus(statusValue);
                              setShowStatusDropdown(false);
                            }}
                            role="option"
                            aria-selected={status === statusValue}
                          >
                            {statusInfo.label}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-6 border border-black/10 shadow-lg">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <KeenIcon
                  icon="shield-tick"
                  className="text-base text-primary"
                />{' '}
                Actions
              </h3>
              <div className="flex flex-col gap-3">
                {(hasPermission('user_feedback_management_update') ||
                  hasPermission('feedback_management_update')) && (
                  <>
                    {mode === 'view' ? (
                      <button
                        onClick={() => setMode('edit')}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium shadow-sm hover:bg-primary-dark"
                        disabled={!feedback}
                        aria-label="Edit feedback"
                      >
                        <KeenIcon icon="pencil" className="text-base" /> Edit
                        Feedback
                      </button>
                    ) : (
                      <button
                        onClick={handleSave}
                        disabled={!hasChanges() || saving || isUpdating}
                        className={clsx(
                          'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium shadow-sm hover:bg-primary-dark',
                          (saving || isUpdating || !hasChanges()) &&
                            'opacity-50 cursor-not-allowed hover:bg-primary'
                        )}
                        aria-label="Save changes"
                      >
                        {(saving || isUpdating) && (
                          <svg
                            className="animate-spin -ml-1 mr-1 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        )}
                        Save Changes
                      </button>
                    )}
                    {!jiraIssueKey && (
                      <button
                        onClick={handleRouteToJsm}
                        disabled={!feedback || isRouting}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-primary/30 text-primary text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Route feedback to Jira Service Management"
                      >
                        <KeenIcon
                          icon="double-right-arrow"
                          className="text-base"
                        />
                        {isRouting ? 'Routing...' : 'Route to JSM'}
                      </button>
                    )}
                  </>
                )}
                {routeError && (
                  <p className="text-sm text-red-600" role="alert">
                    {routeError}
                  </p>
                )}
                <Link
                  to="/user-feedbacks"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gray-100 border border-gray-300 text-sm font-medium text-gray-700 shadow-sm"
                  aria-label="Back to feedback list"
                >
                  <KeenIcon icon="black-left" className="text-base" /> Back to
                  List
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default UserFeedbackDetailsPage;
